import type { TypographyVariantsOptions } from '@mui/material/styles';

/**
 * Type scale (spec §9.1): tight and confident, self-hosted Inter, and nothing
 * below 13px for body content.
 *
 * Sizes are in rem so a reader's browser font-size setting still works — a
 * px-locked scale silently ignores it.
 */
const FONT_STACK = [
  "'Inter Variable'",
  'Inter',
  'system-ui',
  '-apple-system',
  "'Segoe UI'",
  'Roboto',
  'sans-serif',
].join(', ');

export const MONO_STACK = ["'JetBrains Mono'", "'SF Mono'", 'Menlo', 'Consolas', 'monospace'].join(
  ', ',
);

export const typography: TypographyVariantsOptions = {
  fontFamily: FONT_STACK,
  // Inter's optical sizing and tabular figures keep vote counts from jittering
  // as they tick upward.
  fontSize: 15,
  htmlFontSize: 16,

  h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.022em' },
  h2: { fontSize: '1.5rem', fontWeight: 650, lineHeight: 1.2, letterSpacing: '-0.018em' },
  h3: { fontSize: '1.25rem', fontWeight: 650, lineHeight: 1.3, letterSpacing: '-0.014em' },
  h4: { fontSize: '1.125rem', fontWeight: 650, lineHeight: 1.35, letterSpacing: '-0.01em' },
  h5: { fontSize: '1rem', fontWeight: 650, lineHeight: 1.4 },
  h6: { fontSize: '0.9375rem', fontWeight: 650, lineHeight: 1.4 },

  subtitle1: { fontSize: '1rem', fontWeight: 550, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },

  // 15px / 1.6 — the reading size for post bodies.
  body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.6 },
  // 14px, still comfortably above the 13px floor.
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },

  button: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' },
  // The 13px floor: metadata only — timestamps, counts, helper text.
  caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 650,
    lineHeight: 1.4,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
};

/** Smallest size used for body content anywhere, asserted in a test. */
export const MIN_BODY_FONT_PX = 13;
